import React from 'react';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';

import { countTranscodes } from './transcodes';

const Details = styled.div`
   border-top: 1px solid #e5e7eb;
   padding-top: 12px;
`;

const Caption = styled.div`
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: #6b7280;
   margin-bottom: 6px;
`;

const Note = styled.div`
   font-size: 12px;
   color: #6b7280;
`;

// the ladder is compared row by row, so it stays a table and scrolls on its own if it has to
const TableWrapper = styled.div`
   overflow-x: auto;
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 12px;

   th {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 400;
      color: #6b7280;
      text-align: left;
      padding: 0 16px 6px 0;
      white-space: nowrap;
   }

   td {
      color: #374151;
      padding: 6px 16px 6px 0;
      border-top: 1px solid #eef0f2;
      white-space: nowrap;
   }

   tr[data-played='true'] td {
      font-weight: 700;
   }
`;

const PlayedBadge = styled.span`
   background: #10b981;
   color: #ffffff;
   border-radius: 10px;
   padding: 1px 7px;
   margin-left: 8px;
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.5px;
`;

const Muted = styled.span`
   color: #9ca3af;
`;

/**
 * The table column holds the format bintu has configured for a rendition, which a passthrough has
 * none of. For the rendition that is being played the player reports what actually arrives, so that
 * is the more truthful value and the only one available for a played passthrough.
 */
function getResolution(rendition, playedResolution) {
   if (rendition.isPlayed && playedResolution) return playedResolution;
   return rendition.resolution;
}

function getCaption(renditions) {
   const transcodes = countTranscodes(renditions);
   if (transcodes === 0) return 'Stream group - passthrough only, no transcodes';
   return `Stream group - ${transcodes} ${transcodes === 1 ? 'transcode' : 'transcodes'}`;
}

function StreamDetails({ resolution, renditions, isError }) {
   return (
      <Details>
         {isError && <Note>Stream group information is currently unavailable.</Note>}
         {!isError && renditions.length === 0 && <Note>No stream group information available.</Note>}
         {!isError && renditions.length > 0 && (
            <>
               <Caption>{getCaption(renditions)}</Caption>
               <TableWrapper>
                  <Table>
                     <thead>
                        <tr>
                           <th>Index</th>
                           <th>Stream</th>
                           <th>Type</th>
                           <th>Resolution</th>
                           <th>Bitrate</th>
                           <th>FPS</th>
                        </tr>
                     </thead>
                     <tbody>
                        {renditions.map((rendition) => (
                           <tr key={rendition.streamname} data-played={rendition.isPlayed}>
                              <td>{rendition.index}</td>
                              <td>
                                 {rendition.streamname}
                                 {rendition.isPlayed && <PlayedBadge>playing</PlayedBadge>}
                              </td>
                              <td>{rendition.isTranscode ? 'Transcode' : 'Passthrough'}</td>
                              <td>{getResolution(rendition, resolution) || <Muted>same as ingest</Muted>}</td>
                              <td>{rendition.bitrate ? `${rendition.bitrate} kbps` : <Muted>-</Muted>}</td>
                              <td>{rendition.fps ?? <Muted>-</Muted>}</td>
                           </tr>
                        ))}
                     </tbody>
                  </Table>
               </TableWrapper>
            </>
         )}
      </Details>
   );
}

StreamDetails.propTypes = {
   resolution: PropTypes.string,
   renditions: PropTypes.arrayOf(
      PropTypes.shape({
         streamname: PropTypes.string,
         index: PropTypes.number,
         isTranscode: PropTypes.bool,
         resolution: PropTypes.string,
         bitrate: PropTypes.number,
         fps: PropTypes.number,
         isPlayed: PropTypes.bool,
      }),
   ),
   isError: PropTypes.bool,
};

StreamDetails.defaultProps = {
   renditions: [],
};

export default StreamDetails;
